<template>
    <div id="app">
        <template v-if="!isLoginPage">
            <!-- 桌面端布局 -->
            <a-layout v-if="!isMobile" class="layout-father">
                <a-layout-sider theme="light" class="layout-sider" width="240">
                    <left-menu></left-menu>
                </a-layout-sider>

                <a-layout class="layout-right">
                    <a-layout-header class="layout-header">
                        <header-view></header-view>
                    </a-layout-header>
                    <a-layout-content class="layout-content">
                        <router-view class="page-content"/>
                    </a-layout-content>
                </a-layout>
            </a-layout>

            <!-- 移动端布局 -->
            <div v-else class="mobile-layout">
                <header class="mobile-header">
                    <span v-if="showBack" class="mobile-back-btn" @click="goBack">
                        <icon-left />
                    </span>
                    <span class="mobile-page-title">{{ pageTitle }}</span>
                    <span v-if="showBack" class="mobile-home-btn" @click="goHome">
                        <icon-home />
                    </span>
                </header>
                <main class="mobile-content">
                    <router-view/>
                </main>
                <mobile-tab-bar/>
            </div>
        </template>
        <template v-else>
            <router-view />
        </template>
    </div>
</template>

<script>
import {ref, computed, onMounted, onUnmounted} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { IconLeft, IconHome } from '@arco-design/web-vue/es/icon';
import LeftMenu from "@/views/sider-left/LeftMenuView";
import HeaderView from "@/views/header/HeaderView";
import MobileTabBar from "@/components/MobileTabBar";
import { refreshToken, getUserCurrent } from '@/api';

// 底部导航主页面（这些页面不显示返回按钮）
const MAIN_TABS = ['/dashboard', '/payRecord', '/todoRecord', '/document', '/settings'];

export default {
    components: {
        HeaderView,
        LeftMenu,
        MobileTabBar,
        IconLeft,
        IconHome,
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const store = useStore();
        const collapsed = ref(false);
        const isLoginPage = computed(() => route.path === '/login');
        let tokenRefreshTimer = null;

        // 移动端判断（< 768px 视为手机端）
        const mobileQuery = window.matchMedia('(max-width: 768px)');
        const isMobile = ref(mobileQuery.matches);
        const onMobileChange = (e) => {
            isMobile.value = e.matches;
        };

        // 是否显示返回按钮：非底部导航主页面时显示
        const showBack = computed(() => !MAIN_TABS.includes(route.path));

        // 顶部标题：新增/编辑页面按模式区分
        const pageTitle = computed(() => {
            if (route.path === '/payRecord/edit') {
                return route.query.id ? '编辑支出' : '新增支出';
            }
            if (route.path === '/todoRecord/edit') {
                return route.query.id ? '编辑待办' : '新增待办';
            }
            if (route.path === '/accountPassword/edit') {
                return route.query.id ? '编辑账号密码' : '新增账号密码';
            }
            return route.meta.title || '财务管家';
        });

        // 返回
        const goBack = () => {
            if (window.history.length > 1) {
                router.back();
            } else {
                router.push('/dashboard');
            }
        };

        // 回到工作台
        const goHome = () => {
            router.push('/dashboard');
        };

        // 启动时同步一次当前用户信息（含 email，用于待办邮箱通知展示）
        const syncUserInfo = async () => {
            try {
                const res = await getUserCurrent();
                if (res.code === 200 && res.data) {
                    const current = store.getters.userInfo || {};
                    store.commit('setUserInfo', { ...current, ...res.data });
                }
            } catch (error) {
                console.error('同步用户信息失败:', error);
            }
        };

        // 启动 token 刷新定时器
        const startTokenRefreshTimer = () => {
            // 清除旧的定时器
            if (tokenRefreshTimer) {
                clearInterval(tokenRefreshTimer);
            }
            
            // 每隔 1 小时刷新一次 token
            tokenRefreshTimer = setInterval(async () => {
                try {
                    const token = localStorage.getItem('token');
                    if (token) {
                        const res = await refreshToken();
                        if (res.code === 200 && res.data) {
                            // 更新本地 token 和 过期时间
                            localStorage.setItem('token', res.data.token);
                            localStorage.setItem('expireTime', res.data.expireTime);
                            // 同时更新 store
                            store.commit('setToken', res.data.token);
                        }
                    }
                } catch (error) {
                    console.error('刷新 token 失败:', error);
                }
            }, 60 * 60 * 1000); // 60 分钟 = 1 小时
        };

        onMounted(() => {
            // 如果已登录，启动 token 刷新定时器
            if (store.getters.isLoggedIn) {
                startTokenRefreshTimer();
                // 启动时同步一次当前用户信息（刷新页面也能拿到 email 通知邮箱）
                syncUserInfo();
            }
            // 监听窗口宽度变化
            mobileQuery.addEventListener('change', onMobileChange);
        });

        onUnmounted(() => {
            // 组件卸载时清除定时器
            if (tokenRefreshTimer) {
                clearInterval(tokenRefreshTimer);
            }
            mobileQuery.removeEventListener('change', onMobileChange);
        });

        return {
            collapsed,
            isLoginPage,
            isMobile,
            showBack,
            pageTitle,
            goBack,
            goHome,
            toggleCollapse: () => {
                collapsed.value = !collapsed.value;
            },
            startTokenRefreshTimer,
        }
    }
};
</script>

<style>
html, body {
    height: 100%;
    margin: 0;
    padding: 0;
    overflow: hidden;
}
</style>

<style scoped>
#app {
    height: 100vh;
    overflow: hidden;
}

.layout-father {
    height: 100vh;
    overflow: hidden;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
}

.layout-father :deep(.arco-layout-sider),
.layout-father :deep(.arco-layout-layout) {
    display: flex;
    flex-direction: column;
    justify-content: center;
}

/* 左侧导航栏独立滚动 */
.layout-sider {
    height: 100vh !important;
    overflow: hidden;
    background: transparent !important;
}

.layout-sider :deep(.arco-layout-sider) {
    height: 100vh !important;
    min-height: 100vh !important;
    background: transparent !important;
}

/* 右侧布局 */
.layout-right {
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* 右侧顶部固定 */
.layout-header {
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 0 24px;
    height: 64px;
}

/* 右侧内容区域独立滚动 */
.layout-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    padding: 20px;
}

.page-content {
    max-width: 1600px;
    margin: 0 auto;
}

/* ============ 移动端布局 ============ */
.mobile-layout {
    /* 手机浏览器底部工具条/链接栏会动态缩放可视区：
       100vh 按"工具条隐藏"的最大高度算，底栏会被盖住；
       100dvh 随浏览器 UI 实时变化，保证底部导航始终贴合可视区 */
    height: 100vh;  /* 兜底：不支持 dvh 的旧浏览器 */
    height: 100dvh;
    display: flex;
    flex-direction: column;
    background: #f5f7fa;
}

.mobile-header {
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 100;
    height: 52px;
    padding: 0 12px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.mobile-back-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 20px;
    color: #1d2129;
    border-radius: 8px;
    cursor: pointer;
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
    position: absolute;
    left: 8px;
    z-index: 2;
}

.mobile-home-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 20px;
    color: #1d2129;
    border-radius: 8px;
    cursor: pointer;
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
    position: absolute;
    right: 8px;
    z-index: 2;
}

/* 标题绝对居中，不受返回按钮影响 */
.mobile-page-title {
    font-size: 17px;
    font-weight: 700;
    color: #1d2129;
    letter-spacing: -0.3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 70%;
    text-align: center;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
}

.mobile-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    padding: 12px;
    -webkit-overflow-scrolling: touch;
}

/* 移动端：外层容器同样用动态视口高度，避免浏览器底部工具条遮挡（桌面端不受影响） */
@media (max-width: 768px) {
    #app {
        height: 100vh;  /* 兜底 */
        height: 100dvh;
    }
}
</style>

<style>
/* 暗色模式：主布局背景与 header（非 scoped，因选择器含 body） */
body[arco-theme='dark'] .layout-father {
    background: linear-gradient(135deg, #17171a 0%, #232324 100%);
}
body[arco-theme='dark'] .layout-header {
    background: rgba(29, 33, 41, 0.95);
    border-bottom-color: var(--color-border-1);
}

/* 暗色模式：移动端 */
body[arco-theme='dark'] .mobile-layout {
    background: #17171a;
}
body[arco-theme='dark'] .mobile-header {
    background: rgba(29, 33, 41, 0.95);
    border-bottom-color: var(--color-border-1);
}
body[arco-theme='dark'] .mobile-back-btn,
body[arco-theme='dark'] .mobile-home-btn,
body[arco-theme='dark'] .mobile-page-title {
    color: var(--color-text-1);
}
</style>
